import React, { useState, useEffect } from 'react';
import './LoanInputForm.css';

function LoanInputForm({ setCalculationData, setUnpaidInterestOccurred }) {
  const [loanAmount, setLoanAmount] = useState(100000000); // 初期値を10,000万円に設定
  const [interestRate, setInterestRate] = useState(0.6); // 初期金利を0.6%に設定
  const [loanTerm, setLoanTerm] = useState(35); // デフォルトの返済期間を35年に変更
  const [repaymentMethod, setRepaymentMethod] = useState('equalPrincipalInterest');
  const [applyFiveYearRule, setApplyFiveYearRule] = useState(false); // 5年ルール適用のトグル
  const [apply125Rule, setApply125Rule] = useState(false); // 125%ルール適用のトグル
  const [interestRateChanges, setInterestRateChanges] = useState([]);
  const [enableAnnualRateIncrease, setEnableAnnualRateIncrease] = useState(false);
  const [annualRateIncrease, setAnnualRateIncrease] = useState(0.05);
  const [showWarning, setShowWarning] = useState(false); // 警告メッセージ表示用
  
  // 詳細手数料オプション
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [feeStructure, setFeeStructure] = useState('upfrontFee'); // 'upfrontFee' or 'rateMarkup'
  const [upfrontFeeRate, setUpfrontFeeRate] = useState(2.2); // 融資手数料型の手数料率 (%)
  const [rateMarkup, setRateMarkup] = useState(0.2); // 融資金利上乗せ型の金利上乗せ (%)
  const [processingFee, setProcessingFee] = useState(50000); // 融資金利上乗せ型の事務手数料
  const [earlyRepaymentPenaltyRate, setEarlyRepaymentPenaltyRate] = useState(0); // 繰上返済手数料率 (%)
  const [plannedOwnershipYears, setPlannedOwnershipYears] = useState(5); // 計画保有期間
  const [enableComparison, setEnableComparison] = useState(false); // 比較モード

  // localStorage用のキー
  const STORAGE_KEY = 'loanCalculatorParams';

  // パラメータをlocalStorageに保存
  const saveToLocalStorage = () => {
    const params = {
      loanAmount,
      interestRate,
      loanTerm,
      repaymentMethod,
      applyFiveYearRule,
      apply125Rule,
      interestRateChanges,
      enableAnnualRateIncrease,
      annualRateIncrease,
      showAdvancedOptions,
      feeStructure,
      upfrontFeeRate,
      rateMarkup,
      processingFee,
      earlyRepaymentPenaltyRate,
      plannedOwnershipYears,
      enableComparison,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  };

  // localStorageからパラメータを読み込み
  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const params = JSON.parse(saved);
        setLoanAmount(params.loanAmount || 100000000);
        setInterestRate(params.interestRate || 0.6);
        setLoanTerm(params.loanTerm || 35);
        setRepaymentMethod(params.repaymentMethod || 'equalPrincipalInterest');
        setApplyFiveYearRule(params.applyFiveYearRule || false);
        setApply125Rule(params.apply125Rule || false);
        setInterestRateChanges(params.interestRateChanges || []);
        setEnableAnnualRateIncrease(params.enableAnnualRateIncrease || false);
        setAnnualRateIncrease(params.annualRateIncrease ?? 0.05);
        setShowAdvancedOptions(params.showAdvancedOptions || false);
        setFeeStructure(params.feeStructure || 'upfrontFee');
        setUpfrontFeeRate(params.upfrontFeeRate || 2.2);
        setRateMarkup(params.rateMarkup || 0.2);
        setProcessingFee(params.processingFee || 50000);
        setEarlyRepaymentPenaltyRate(params.earlyRepaymentPenaltyRate ?? 0);
        setPlannedOwnershipYears(params.plannedOwnershipYears || 5);
        setEnableComparison(params.enableComparison || false);
      }
    } catch (error) {
      console.error('LocalStorage読み込みエラー:', error);
    }
  };

  // コンポーネントマウント時にlocalStorageから読み込み
  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  const handleLoanAmountChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (!isNaN(value)) {
      setLoanAmount(Number(value) * 10000);
    }
  };

  const formatNumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // 「元金均等返済」で5年ルールや125%ルールが選択されている場合、警告を表示
    if (
      repaymentMethod === 'equalPrincipal' &&
      (applyFiveYearRule || apply125Rule)
    ) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }

    const loanData = {
      loanAmount,
      interestRate,
      loanTerm,
      repaymentMethod,
      interestRateChanges,
      enableAnnualRateIncrease,
      annualRateIncrease,
      applyFiveYearRule,
      apply125Rule,
      // 詳細手数料オプション - showAdvancedOptionsが有効な場合のみ含める
      feeStructure: showAdvancedOptions ? feeStructure : null,
      upfrontFeeRate,
      rateMarkup,
      processingFee,
      earlyRepaymentPenaltyRate,
      plannedOwnershipYears,
      enableComparison: showAdvancedOptions ? enableComparison : false,
    };

    const result = calculateLoan(loanData);
    setCalculationData(result);

    // 未払い利息が発生したかを確認
    const unpaidInterestOccurred = result.repayments.some(r => r.unpaidInterest > 0);
    setUnpaidInterestOccurred(unpaidInterestOccurred);

    // パラメータをlocalStorageに保存
    saveToLocalStorage();
  };

  const calculateLoan = (data) => {
    const {
      loanAmount,
      interestRate,
      loanTerm,
      repaymentMethod,
      interestRateChanges,
      enableAnnualRateIncrease,
      annualRateIncrease,
      applyFiveYearRule,
      apply125Rule,
      feeStructure,
      upfrontFeeRate,
      rateMarkup,
      processingFee,
      earlyRepaymentPenaltyRate,
      plannedOwnershipYears,
      enableComparison,
    } = data;

    const monthlyTerm = loanTerm * 12;
    let remainingBalance = loanAmount;
    let currentRate = interestRate; // 現在の金利を追跡
    
    // 手数料構造に応じて金利を調整
    let effectiveRate = currentRate;
    if (feeStructure === 'rateMarkup') {
      effectiveRate = currentRate + rateMarkup;
    }
    
    let monthlyInterestRate = effectiveRate / 100 / 12;
    const repayments = [];
    const balanceOverTime = [];

    let initialMonthlyPayment = 0;

    if (repaymentMethod === 'equalPrincipalInterest') {
      initialMonthlyPayment =
        (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, monthlyTerm)) /
        (Math.pow(1 + monthlyInterestRate, monthlyTerm) - 1);
    } else {
      // 元金均等返済の場合は、毎月の元金返済額を計算
      var principalPaymentPerMonth = loanAmount / monthlyTerm;
    }

    let fiveYearFixedPayment = initialMonthlyPayment;
    let rateChangeAppliedMonth = null;
    let totalPayment = initialMonthlyPayment;
    let previousRate = null;

    const cleanedInterestRateChanges = [...interestRateChanges]
      .map((change) => ({
        year: Number(change.year),
        rate: Number(change.rate),
      }))
      .filter((change) => Number.isFinite(change.year) && change.year > 0 && Number.isFinite(change.rate));

    const annualPatternChanges = [];
    if (enableAnnualRateIncrease && Number.isFinite(annualRateIncrease) && annualRateIncrease !== 0) {
      for (let year = 1; year <= loanTerm; year++) {
        annualPatternChanges.push({
          year,
          rate: interestRate + annualRateIncrease * year,
        });
      }
    }

    // 同一年の設定は手動入力を優先
    const rateChangesByYear = new Map();
    annualPatternChanges.forEach((change) => {
      rateChangesByYear.set(change.year, change);
    });
    cleanedInterestRateChanges.forEach((change) => {
      rateChangesByYear.set(change.year, change);
    });
    const allRateChanges = Array.from(rateChangesByYear.values()).sort((a, b) => a.year - b.year);

    let changesIndex = 0;

    let unpaidInterest = 0; // 未払い利息の累積

    // 5年ルールの管理用
    let nextPaymentIncreaseMonth =
      applyFiveYearRule && repaymentMethod === 'equalPrincipalInterest' ? 61 : null;

    // 総返済額と総利息額の初期化
    let totalInterestPaid = 0;
    let totalPrincipalPaid = 0;

    for (let month = 1; month <= monthlyTerm; month++) {
      // 利率変更の確認と適用
      if (
        changesIndex < allRateChanges.length &&
        month === allRateChanges[changesIndex].year * 12 + 1
      ) {
        // 変更前の金利を保存
        previousRate = currentRate;
        currentRate = allRateChanges[changesIndex].rate;
        
        // 手数料構造に応じて実効金利を計算
        effectiveRate = currentRate;
        if (feeStructure === 'rateMarkup') {
          effectiveRate = currentRate + rateMarkup;
        }
        
        monthlyInterestRate = effectiveRate / 100 / 12;
        changesIndex++;

        if (repaymentMethod === 'equalPrincipalInterest') {
          const remainingTerm = monthlyTerm - month + 1;

          // 新しい金利で返済額を再計算
          initialMonthlyPayment =
            (remainingBalance * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, remainingTerm)) /
            (Math.pow(1 + monthlyInterestRate, remainingTerm) - 1);

          rateChangeAppliedMonth = month;
        }
      }

      let interestPayment = remainingBalance * monthlyInterestRate;
      let principalPayment;
      let is125RuleAppliedThisMonth = false;

      if (repaymentMethod === 'equalPrincipalInterest') {
        // 5年ルールの適用
        if (applyFiveYearRule && month < nextPaymentIncreaseMonth) {
          totalPayment = fiveYearFixedPayment;
        } else if (applyFiveYearRule && month === nextPaymentIncreaseMonth) {
          // 見直し月：現在の残高と金利で返済額を再計算
          const remainingTerm = monthlyTerm - month + 1;
          let reviewedPayment =
            (remainingBalance * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, remainingTerm)) /
            (Math.pow(1 + monthlyInterestRate, remainingTerm) - 1);

          if (apply125Rule) {
            const maxPayment = fiveYearFixedPayment * 1.25;
            if (reviewedPayment > maxPayment) {
              reviewedPayment = maxPayment;
              is125RuleAppliedThisMonth = true;
            }
          }
          totalPayment = reviewedPayment;
          fiveYearFixedPayment = reviewedPayment;
          nextPaymentIncreaseMonth += 60;
        } else {
          totalPayment = initialMonthlyPayment;
        }

        principalPayment = totalPayment - interestPayment;
      } else {
        // 元金均等返済
        principalPayment = principalPaymentPerMonth;
        totalPayment = principalPayment + interestPayment;
      }

      // 未払い利息の計算
      if (principalPayment < 0) {
        unpaidInterest += -principalPayment;
        principalPayment = 0;
      }

      // 元金返済額が残高を超えないように調整
      if (principalPayment > remainingBalance) {
        principalPayment = remainingBalance;
        totalPayment = principalPayment + interestPayment;
      }

      remainingBalance -= principalPayment;

      if (remainingBalance < 0) {
        remainingBalance = 0;
      }

      // 総利息額と総元金返済額の更新
      totalInterestPaid += interestPayment;
      totalPrincipalPaid += principalPayment;

      repayments.push({
        month,
        interestPayment,
        principalPayment,
        totalPayment,
        unpaidInterest, // 未払い利息を追加
        isFiveYearRuleApplied:
          applyFiveYearRule &&
          repaymentMethod === 'equalPrincipalInterest' &&
          month < nextPaymentIncreaseMonth,
        is125RuleApplied: is125RuleAppliedThisMonth,
        isRateChangeMonth: month === rateChangeAppliedMonth, // 金利変更の月を示すフラグを追加
        previousRate: month === rateChangeAppliedMonth ? previousRate : null,
        newRate: month === rateChangeAppliedMonth ? currentRate : null,
      });

      balanceOverTime.push({
        month,
        remainingBalance: remainingBalance / 10000, // 残高の単位を万円に変換
        unpaidInterest, // 未払い利息を追加
        isFiveYearRuleApplied:
          applyFiveYearRule &&
          repaymentMethod === 'equalPrincipalInterest' &&
          month < nextPaymentIncreaseMonth,
        is125RuleApplied: is125RuleAppliedThisMonth,
        isRateChangeMonth: month === rateChangeAppliedMonth, // 金利変更の月を示すフラグ
        previousRate: month === rateChangeAppliedMonth ? previousRate : null,
        newRate: month === rateChangeAppliedMonth ? currentRate : null,
      });

      if (remainingBalance <= 0 && unpaidInterest <= 0) {
        break;
      }
    }

    const totalPaymentAmount = totalInterestPaid + totalPrincipalPaid;

    // 手数料計算 - feeStructureがnullでない場合のみ計算
    let upfrontFee = 0;
    let plannedRepaymentMonths = 0;
    let remainingBalanceAtSale = 0;
    let earlyRepaymentPenalty = 0;
    let comparisonData = null;
    
    if (feeStructure) {
      upfrontFee = feeStructure === 'upfrontFee' ? loanAmount * (upfrontFeeRate / 100) : processingFee;
      plannedRepaymentMonths = Math.min(plannedOwnershipYears * 12, monthlyTerm);
      remainingBalanceAtSale = remainingBalance > 0 ? remainingBalance : 
        balanceOverTime[Math.min(plannedRepaymentMonths - 1, balanceOverTime.length - 1)]?.remainingBalance * 10000 || 0;
      earlyRepaymentPenalty = remainingBalanceAtSale * (earlyRepaymentPenaltyRate / 100);
      
      // 比較用の計算（両方の手数料構造での総コスト）
      if (enableComparison) {
        const alternativeFeeStructure = feeStructure === 'upfrontFee' ? 'rateMarkup' : 'upfrontFee';
        const alternativeData = {
          ...data,
          feeStructure: alternativeFeeStructure,
          enableComparison: false, // 無限ループを防ぐ
        };
        comparisonData = calculateLoan(alternativeData);
      }
    }

    return {
      repayments,
      balanceOverTime,
      totalInterestPaid,
      totalPrincipalPaid,
      totalPaymentAmount,
      // 手数料関連のデータ
      feeStructure,
      upfrontFee,
      earlyRepaymentPenalty,
      remainingBalanceAtSale,
      plannedRepaymentMonths,
      totalCostWithFees: feeStructure ? totalPaymentAmount + upfrontFee + earlyRepaymentPenalty : totalPaymentAmount,
      comparisonData,
    };
  };

  const addInterestRateChange = () => {
    setInterestRateChanges([...interestRateChanges, { year: '', rate: '' }]);
  };

  const handleInterestRateChange = (index, field, value) => {
    const newChanges = [...interestRateChanges];
    newChanges[index][field] = value;
    setInterestRateChanges(newChanges);
  };

  const removeInterestRateChange = (index) => {
    const newChanges = [...interestRateChanges];
    newChanges.splice(index, 1);
    setInterestRateChanges(newChanges);
  };

  return (
    <form onSubmit={handleSubmit} className="loan-form">
      <h2>住宅ローンシミュレーター</h2>
      <div className="form-group">
        <label>
          借入金額 (万円):
          <input
            type="text"
            value={formatNumberWithCommas(loanAmount / 10000)}
            onChange={handleLoanAmountChange}
            required
          />
        </label>
        <label>
          利率 (%):
          <input
            type="number"
            step="0.01"
            value={interestRate}
            onChange={(e) => setInterestRate(Number(e.target.value))}
            required
          />
        </label>
      </div>
      <div className="form-group">
        <label>
          返済期間 (年):
          <input
            type="number"
            value={loanTerm}
            onChange={(e) => setLoanTerm(Number(e.target.value))}
            required
          />
        </label>
        <label>
          返済方法:
          <select
            value={repaymentMethod}
            onChange={(e) => setRepaymentMethod(e.target.value)}
          >
            <option value="equalPrincipalInterest">元利均等返済</option>
            <option value="equalPrincipal">元金均等返済</option>
          </select>
        </label>
      </div>
      <div className="form-group">
        <label className="checkbox-label">
          5年ルールを適用する
          <input
            type="checkbox"
            checked={applyFiveYearRule}
            onChange={(e) => {
              setApplyFiveYearRule(e.target.checked);
              handleSubmit(e); // トグル変更時に再計算を実行
            }}
          />
        </label>
        <label className="checkbox-label">
          125％ルールを適用する
          <input
            type="checkbox"
            checked={apply125Rule}
            onChange={(e) => {
              setApply125Rule(e.target.checked);
              handleSubmit(e); // トグル変更時に再計算を実行
            }}
          />
        </label>
        <label className="checkbox-label">
          詳細手数料計算を有効にする
          <input
            type="checkbox"
            checked={showAdvancedOptions}
            onChange={(e) => setShowAdvancedOptions(e.target.checked)}
          />
        </label>
      </div>

      {/* 警告メッセージを表示 */}
      {showWarning && (
        <div className="warning">
          <p>
            「元金均等返済」を選択した場合、5年ルールや125％ルールは適用されないことが一般的です。<br />
            以下の計算では5年ルール及び125%ルールは選択にかかわらず適用していません。
          </p>
        </div>
      )}

      <h3>利率変更の設定</h3>
      <label className="checkbox-label">
        毎年一定幅で金利を増加させる
        <input
          type="checkbox"
          checked={enableAnnualRateIncrease}
          onChange={(e) => setEnableAnnualRateIncrease(e.target.checked)}
        />
      </label>
      {enableAnnualRateIncrease && (
        <div className="form-group">
          <label>
            毎年の増加幅 (%):
            <input
              type="number"
              step="0.01"
              value={annualRateIncrease}
              onChange={(e) => setAnnualRateIncrease(Number(e.target.value))}
            />
          </label>
        </div>
      )}
      {interestRateChanges.map((change, index) => (
        <div key={index} className="interest-rate-change">
          <label>
            年数:
            <input
              type="number"
              value={change.year}
              onChange={(e) =>
                handleInterestRateChange(index, 'year', Number(e.target.value))
              }
              required
            />
          </label>
          <label>
            新しい利率 (%):
            <input
              type="number"
              step="0.01"
              value={change.rate}
              onChange={(e) =>
                handleInterestRateChange(index, 'rate', Number(e.target.value))
              }
              required
            />
          </label>
          <button type="button" onClick={() => removeInterestRateChange(index)} className="remove-button">
            削除
          </button>
        </div>
      ))}
      <button type="button" onClick={addInterestRateChange} className="add-button">
        利率変更を追加
      </button>

      {/* 詳細手数料オプション */}
      {showAdvancedOptions && (
        <div className="advanced-options-section">
          <h3>詳細手数料オプション</h3>
          <div className="advanced-options">
            <div className="form-group">
              <label>
                手数料構造:
                <select
                  value={feeStructure}
                  onChange={(e) => setFeeStructure(e.target.value)}
                >
                  <option value="upfrontFee">融資手数料型</option>
                  <option value="rateMarkup">融資金利上乗せ型</option>
                </select>
              </label>
            </div>

            {feeStructure === 'upfrontFee' ? (
              <div className="form-group">
                <label>
                  融資手数料率 (%):
                  <input
                    type="number"
                    step="0.1"
                    value={upfrontFeeRate}
                    onChange={(e) => setUpfrontFeeRate(Number(e.target.value))}
                  />
                </label>
              </div>
            ) : (
              <div className="form-group">
                <label>
                  金利上乗せ (%):
                  <input
                    type="number"
                    step="0.01"
                    value={rateMarkup}
                    onChange={(e) => setRateMarkup(Number(e.target.value))}
                  />
                </label>
                <label>
                  事務手数料 (円):
                  <input
                    type="number"
                    value={processingFee}
                    onChange={(e) => setProcessingFee(Number(e.target.value))}
                  />
                </label>
              </div>
            )}

            <div className="form-group">
              <label>
                計画保有期間 (年):
                <input
                  type="number"
                  value={plannedOwnershipYears}
                  onChange={(e) => setPlannedOwnershipYears(Number(e.target.value))}
                />
              </label>
              <label>
                繰上返済手数料率 (%):
                <input
                  type="number"
                  step="0.1"
                  value={earlyRepaymentPenaltyRate}
                  onChange={(e) => setEarlyRepaymentPenaltyRate(Number(e.target.value))}
                />
              </label>
            </div>

            <label className="checkbox-label">
              手数料構造を比較表示する
              <input
                type="checkbox"
                checked={enableComparison}
                onChange={(e) => setEnableComparison(e.target.checked)}
              />
            </label>
          </div>
        </div>
      )}

      <br />
      <button type="submit" className="submit-button">計算</button>

      {/* 免責事項を追加 */}
      <div className="disclaimer">
        <p>
          ※この計算結果はあくまで概算であり、実際のローン条件とは異なる場合があります。正確な情報については金融機関等の専門家にご相談ください。
        </p>
        <p>
          ※複数の利率変更を追加した場合、計算結果が正確でない可能性があります。詳細なシミュレーションには専門家の助言を求めてください。
        </p>
      </div>
    </form>
  );
}

export default LoanInputForm;
